import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import { toast } from "react-toastify";

import Button from "../Button";
import HelpText from "../quiz/HelpText";
import { getSpeechConfig, textToSpeech } from "../../store/quiz/quizBaseApi";
import { generateUUID } from "../../utils";
import quizPlayIcon from "../../assets/images/quiz/speech-play.svg";
import quizPauseIcon from "../../assets/images/quiz/speech-pause.svg";
import quizReplayIcon from "../../assets/images/quiz/speech-retry.svg";
import speechLoadingIcon from "../../assets/images/quiz/speech-loading.gif";

const iconStyle = { marginRight: "1rem" };
const PlayIcon = () => <img style={iconStyle} src={quizPlayIcon} />;
const PauseIcon = () => <img style={iconStyle} src={quizPauseIcon} />;
const LoadingIcon = () => (
  <img style={iconStyle} height={"50px"} src={speechLoadingIcon} />
);

const useStyles = makeStyles()((theme) => {
  return {
    root: {
      textAlign: "left",
      display: "flex",
      flexWrap: "wrap", // Add this
      width: "100%", // Add this
    },
    text: {
      marginTop: "auto",
      marginBottom: "auto",
      lineHeight: "25px",
      display: "inline", // Add this
      whiteSpace: "nowrap", // Keep individual words together
      overflowWrap: "anywhere", // but still break a single pathologically long token
    },
    // Play button + text as a 2-column grid. Row 1 holds the button and the
    // first authored line, centred against each other; any further lines go
    // in row 2 under the text. Grid "auto" columns shrink to the widest line,
    // so the button sits right beside the text and the pair stays centred.
    // (The root wraps for helpText; a long question used to drop below the
    // button there, and a single wrapping text row filled the full width and
    // pushed the button to the edge.)
    playRow: {
      display: "grid",
      gridTemplateColumns: "auto minmax(0, auto)",
      alignItems: "center",
      rowGap: "0.25rem",
      width: "100%",
      justifyContent: "inherit",
    },
    playButtonCell: { gridRow: 1, gridColumn: 1 },
    firstLine: {
      gridRow: 1,
      gridColumn: 2,
      justifyContent: "inherit", // passes the root's alignment to textLine
    },
    restLines: {
      gridRow: 2,
      gridColumn: 2,
      display: "flex",
      flexDirection: "column",
      gap: "0.25rem",
      justifyContent: "inherit",
    },
    textLine: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "flex-start",
      gap: "0.25rem",
      justifyContent: "inherit",
    },
  };
});

export default function TextToSpeech({
  content = "",
  autoPlay,
  onAudioPlayback,
  onAudioPlaybackComplete,
  helpText,
  styles,
  middle = false,
}) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [contentArray, setContentArray] = useState([]);
  const [audioUrls, setAudioUrls] = useState([]);
  const [sentanceAudio, setSentanceAudio] = useState("");
  const [currentIndex, setCurrentIndex] = useState(null);
  const [sentanceAudioPlaying, setSentanceAudioPlaying] = useState(false);

  const audioRef = useRef(new Audio());
  const words = useRef({
    word_data: {},
    total_words: 0,
  });
  const referenceId = useRef(generateUUID());

  const student = JSON.parse(localStorage.getItem("profile")) || {};
  const played = useRef(false);
  const timeout = useRef(null);
  const sentenceAudioRef = useRef(null);

  const { classes } = useStyles();

  const playNext = () => {
    if (audioQueue.length > 0) {
      const nextAudioUrl = audioQueue.shift(); // Dequeue the next audio
      audio.src = URL.createObjectURL(nextAudioUrl.blob);

      audio
        .play()
        .then(() => {
          setIsPlaying(true);
          console.log("Playing:", nextAudioUrl);
        })
        .catch((err) => {
          console.error("Audio playback error:", err);
          playNext(); // Try playing the next audio
        });
    } else {
      console.log("Audio queue is empty.");
    }
  };

  const togglePlayPause = () => {
    if (played.current) return;

    if (isPlaying) {
      // audioRef.current.pause();
    } else {
      // audioRef.current.play();
      if (
        !played.current &&
        onAudioPlayback &&
        typeof onAudioPlayback === "function"
      ) {
        onAudioPlayback();
      }

      setCurrentIndex(0);
      played.current = true;
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleFullSentancePlayback = () => {
    setIsPlaying(false);
    setSentanceAudioPlaying(true);

    console.log(
      "Playing full sentance audio:",
      sentanceAudio,
      sentanceAudio.length,
    );

    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }

    if (sentanceAudio && sentanceAudio.length) {
      // Fix the S3 endpoint - new s3 endpoint change --- minimal fix for audio play
      const correctedAudioUrl = sentanceAudio.replace(
        "knowledgebasechat.s3.eu-west-1.amazonaws.com",
        "knowledgebasechat.s3-us-west-1.amazonaws.com",
      );
      const audio = new Audio(correctedAudioUrl);
      sentenceAudioRef.current = audio;
      audio.onended = () => {
        setSentanceAudioPlaying(false);
        if (sentenceAudioRef.current === audio) {
          sentenceAudioRef.current = null;
        }
      };
      audio.play();
    } else {
      console.error(
        "Sentance audio is not available. adding timeout for 500ms",
      );
      timeout.current = setTimeout(() => handleFullSentancePlayback(), 500);
    }
  };

  const handleEnd = () => {
    // setIsPlaying(false);

    const total_words = words.current.total_words;
    if (currentIndex < total_words - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      played.current = false;
      setCurrentIndex(null);

      handleFullSentancePlayback();

      if (
        onAudioPlaybackComplete &&
        typeof onAudioPlaybackComplete === "function"
      ) {
        onAudioPlaybackComplete();
      }
    }
  };

  useEffect(() => {
    if (!content) return;

    referenceId.current = generateUUID();
    words.current = { word_data: {}, total_words: 0 };
    played.current = false;
    setAudioUrls([]);
    setContentArray([]);
    setAudioUrl(null);
    setCurrentIndex(null);
    setSentanceAudio("");
    setIsLoading(true);
    setIsPlaying(false);
    setSentanceAudioPlaying(false);

    try { audioRef.current?.pause(); } catch (_) {}
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
    if (sentenceAudioRef.current) {
      try { sentenceAudioRef.current.pause(); } catch (_) {}
      sentenceAudioRef.current = null;
    }

    // Normalize escaped "\n" coming from content authoring, then split into
    // lines so explicit line breaks survive into the rendered output.
    const normalizedContent = String(content).replace(/\\n/g, "\n");
    const tokens = [];
    normalizedContent.split(/\r?\n/).forEach((line, lineIdx) => {
      line
        .split(/\s+/)
        .filter(Boolean)
        .forEach((word, wordIdx) => {
          tokens.push({ word, breakBefore: lineIdx > 0 && wordIdx === 0 });
        });
    });

    let splitted = tokens.map((t) => t.word);
    let socketConn = window?.socketConn;
    let wordsReceived = 0;

    const handleReceiveAudio = (data) => {
      try {
        let jsonString;
        if (data instanceof ArrayBuffer) {
          const decoder = new TextDecoder("utf-8");
          jsonString = decoder.decode(data);
        } else {
          jsonString = data.toString("utf-8");
        }

        const parsedData = JSON.parse(jsonString);
        const {
          status,
          word,
          audio: audioBase64,
          error: receivedError,
          reference_id,
        } = parsedData;

        if (status === "error") {
          const errorMessage = receivedError || "Unknown error occurred.";
          toast.error(`Error received: ${errorMessage}`);
          socketConn.disconnect();
          return;
        }

        if (word && audioBase64 && referenceId.current == reference_id) {
          // console.log(`Received word: ${word}`);
          words.current.total_words++;
          if (
            words.current.word_data[word] &&
            words.current.word_data[word].blob
          )
            return;

          const audioData = new Uint8Array(
            atob(audioBase64)
              .split("")
              .map((char) => char.charCodeAt(0)),
          );
          const blob = new Blob([audioData], { type: "audio/wav" });
          if (!words.current.word_data[word]) {
            words.current.word_data[word] = { word };
          }

          words.current.word_data[word].blob = blob;
          wordsReceived++;
        }
        if (splitted.length >= wordsReceived) {
          setIsLoading(false);
          setAudioUrls(Object.values(words.current.word_data));
        }
      } catch (err) {
        console.error("Error processing the received message:", err);
      }
    };

    async function f() {
      const conf = await getSpeechConfig(
        String(student.geographicalType).toLowerCase(),
        student.learningGeneration,
      );
      textToSpeech({
        text: normalizedContent,
        accent: conf.accent || "EN_US",
        gender: conf.gender || "Female",
      }).then((res) => {
        if (res?.response?.audio) {
          setSentanceAudio(res.response.audio);
        }
      });

      // Create array with index and word for unique identification
      const wordsWithIndex = tokens.map((t, index) => ({
        word: t.word,
        breakBefore: t.breakBefore,
        index,
        blob: null,
      }));
      setContentArray(wordsWithIndex);

      // Track unique words for audio requests
      const uniqueWords = [...new Set(splitted)];
      uniqueWords.forEach(
        (word) => (words.current.word_data[word] = { word, blob: null }),
      );

      // splitted.forEach(e => words.current[e] = { word: e, blob: null });

      // setContentArray(splitted);

      if (!socketConn) {
        console.error("Socket connection is not available.");
        return;
      }

      // Handle server responses
      socketConn.on("receiveAudio", handleReceiveAudio);

      // Emit the text to start processing
      // console.log("Sending text:", content);
      socketConn.emit("sendText", {
        text: content,
        accent: conf.accent?.slice(3) || "IN",
        gender: String(conf.gender).toUpperCase(),
        reference_id: referenceId.current,
      });
    }
    f();

    return () => {
      if (socketConn) {
        socketConn.off("receiveAudio", handleReceiveAudio);
      }
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
      if (sentenceAudioRef.current) {
        try { sentenceAudioRef.current.pause(); } catch (_) {}
        sentenceAudioRef.current = null;
      }
      try { audioRef.current?.pause(); } catch (_) {}
    };
  }, [content]);

  useEffect(() => {
    if (currentIndex != null && contentArray[currentIndex]) {
      const currentWord = contentArray[currentIndex].word;
      const wordAudio = words.current.word_data[currentWord];

      if (!wordAudio || !wordAudio.blob) return;

      audioRef.current.src = URL.createObjectURL(wordAudio.blob);
      audioRef.current.play();
    }
  }, [currentIndex, contentArray]);

  // Split at authored newlines, keeping each word's index for highlighting.
  const lines = React.useMemo(() => {
    const out = [];
    contentArray.forEach((item, i) => {
      if (!out.length || item.breakBefore) out.push([]);
      out[out.length - 1].push({ item, i });
    });
    return out;
  }, [contentArray]);

  const renderLine = (line) => (
    <div className={classes.textLine}>
      {line.map(({ item, i }) => (
        <span
          key={`${item.word}-${i}`}
          style={{
            ...(currentIndex === i
              ? {
                  // Use index comparison instead of word comparison
                  background: "rgba(255, 199, 74, 0.2)",
                  border: "1px solid #FFC74A",
                  borderRadius: "10px",
                  padding: "2px 6px",
                  lineHeight: "2.6",
                }
              : {}),
          }}
          className={classes.text}
        >
          {item.word}
        </span>
      ))}
    </div>
  );

  return (
    <div className={"label-text " + classes.root} style={styles}>
      <div className={classes.playRow}>
      <div className={classes.playButtonCell}>
        <Button
          endIcon={
            isPlaying ? (
              <PauseIcon />
            ) : isLoading ? (
              <LoadingIcon />
            ) : (
              <PlayIcon />
            )
          }
          size={"small"}
          type={"secondary"}
          className={classes.button}
          onClick={togglePlayPause}
          disabled={isLoading || sentanceAudioPlaying}
        />
      </div>
      <div className={classes.firstLine}>{renderLine(lines[0] || [])}</div>
      {lines.length > 1 && (
        <div className={classes.restLines}>
          {lines.slice(1).map((line, n) => (
            <React.Fragment key={n}>{renderLine(line)}</React.Fragment>
          ))}
        </div>
      )}
      </div>
      {helpText && Object.keys(helpText).length > 0 && (
        <HelpText className={classes.helpText} data={helpText} />
      )}
      <audio
        ref={audioRef}
        src={audioUrl}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnd}
      />
    </div>
  );
}
